/**
 * Identity & status card — edit a room's name / country / lifecycle status
 * (PATCH /api/rooms/:id). Moving a room to a non-active status (disabled or
 * archived) is consequential, so it goes through a confirm modal first.
 */

import { useState } from 'react';
import Button from '@8x8/oxygen-button';
import Select from '@8x8/oxygen-select';
import type { SelectOption } from '@8x8/oxygen-select';
import TextField from '@8x8/oxygen-text-field';
import type { Room, RoomStatus } from '@spaces/contracts';
import { updateRoom } from '../../api';
import { useToast } from '../../toast';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { Card, CardTitle, FieldLabel } from '../../ui/primitives';

const STATUSES: RoomStatus[] = ['provisioning', 'active', 'disabled', 'archived'];
const LABEL: Record<RoomStatus, string> = {
    provisioning: 'Provisioning',
    active: 'Active',
    disabled: 'Disabled',
    archived: 'Archived'
};
const STATUS_OPTIONS: SelectOption<RoomStatus>[] = STATUSES.map(s => ({ value: s, label: LABEL[s] }));

function isDestructive(target: RoomStatus, current: RoomStatus): boolean {
    return (target === 'disabled' || target === 'archived') && target !== current;
}

export function IdentityCard({ room, onSaved }: { room: Room; onSaved: (room: Room) => void }): JSX.Element {
    const toast = useToast();
    const [name, setName] = useState(room.name);
    const [country, setCountry] = useState(room.countryCode ?? '');
    const [status, setStatus] = useState<RoomStatus>(room.status);
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const save = async (): Promise<void> => {
        setBusy(true);
        setConfirmOpen(false);

        const result = await updateRoom(room.id, {
            name: name.trim(),
            countryCode: country.trim(),
            status
        });

        setBusy(false);

        if (!result.ok) {
            toast.notify('error', `Couldn't save: ${result.message}`);

            return;
        }

        toast.notify('success', 'Saved.');
        onSaved(result.data);
    };

    const onSaveClick = (): void => {
        if (isDestructive(status, room.status)) {
            setConfirmOpen(true);
        } else {
            void save();
        }
    };

    const currentOption = STATUS_OPTIONS.find(o => o.value === status) ?? STATUS_OPTIONS[0];

    return (
        <Card>
            <CardTitle>Identity &amp; status</CardTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                    <TextField
                        label="Room name"
                        value={name}
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <TextField
                            label="Country code"
                            value={country}
                            placeholder="ISO 3166-1 alpha-2"
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCountry(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <FieldLabel>Status</FieldLabel>
                        <Select
                            options={STATUS_OPTIONS}
                            value={currentOption}
                            isSearchable={false}
                            isClearable={false}
                            onChange={(newValue: unknown) => {
                                const opt = newValue as SelectOption<RoomStatus> | null;

                                if (opt?.value) {
                                    setStatus(opt.value);
                                }
                            }}
                        />
                    </div>
                </div>
                <div>
                    <Button variant="primary" isDisabled={busy} onClick={onSaveClick}>
                        {busy ? 'Saving…' : 'Save changes'}
                    </Button>
                </div>
            </div>

            <ConfirmModal
                open={confirmOpen}
                title="Change room status?"
                confirmLabel="Change status"
                destructive
                busy={busy}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => void save()}
            >
                Setting this room to <strong>{LABEL[status].toLowerCase()}</strong> stops it accepting new
                pairings. You can re-enable it later.
            </ConfirmModal>
        </Card>
    );
}
